<template>
  <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
    <header class="px-4 py-3 bg-gray-900/50 border-b border-gray-700">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-sm font-bold text-gray-300">{{ title }}</h3>
          <p class="text-[11px] text-gray-400 mt-1">{{ subtitle }}</p>
        </div>

        <div class="flex flex-wrap gap-2 text-xs">
          <div class="flex gap-1 ml-2 border-l border-gray-600 pl-2">
            <button
                v-for="src in sourceOptions"
                :key="src.value"
                @click="selectSource(src.value)"
                class="px-3 py-1.5 rounded-full border transition-all text-xs"
                :class="selectedSource === src.value
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'"
              >
              {{ src.label }}
            </button>
          </div>

          <button
              v-for="option in periodOptions"
              :key="option.value"
              @click="selectPeriod(option.value)"
              class="px-3 py-1.5 rounded-full border transition-all"
              :class="selectedPeriod === option.value
              ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
              : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </header>

    <div v-if="loading" class="p-8 text-center">
      <i class="fas fa-circle-notch fa-spin text-blue-400 text-2xl"></i>
      <p class="text-gray-400 mt-2 text-sm">در حال دریافت اطلاعات...</p>
    </div>

    <div v-else-if="error" class="p-8 text-center">
      <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
      <p class="text-red-400 mt-2 text-sm">{{ error }}</p>
      <button @click="fetchData" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-xs">
        تلاش مجدد
      </button>
    </div>

    <div v-else class="p-4">
      <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-4">
        <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h4 class="text-xs font-semibold text-gray-300">{{ getPeriodLabel() }}</h4>
          <div class="flex gap-4 text-xs">
            <span class="flex items-center gap-1">
              <span class="w-3 h-0.5 bg-emerald-500 inline-block rounded"></span>
              <span class="text-gray-400">{{ dataset1Label }}</span>
            </span>
            <span class="flex items-center gap-1">
              <span class="w-3 h-0.5 bg-gray-500 inline-block rounded"></span>
              <span class="text-gray-400">{{ dataset2Label }}</span>
            </span>
          </div>
        </div>

        <div class="relative h-72">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div class="bg-gradient-to-r from-emerald-900/20 to-emerald-800/10 border border-emerald-800/30 rounded-xl p-3">
          <p class="text-xs text-gray-400">{{ dataset1Label }} - مجموع</p>
          <p class="text-xl font-bold text-emerald-400">{{ formatNumber(currentDataset1Total) }} <span class="text-xs text-gray-500">تومان</span></p>
        </div>
        <div class="bg-gradient-to-r from-gray-400/20 to-store-900/10 border border-gray-500/30 rounded-xl p-3">
          <p class="text-xs text-gray-400">{{ dataset2Label }} - مجموع</p>
          <p class="text-xl font-bold text-gray-100">{{ formatNumber(currentDataset2Total) }} <span class="text-xs text-gray-300">تومان</span></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_CHART_DATA = {
  hourly: {
    labels: ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۱۰', '۱۱', '۱۲', '۱۳', '۱۴', '۱۵', '۱۶', '۱۷', '۱۸', '۱۹', '۲۰', '۲۱', '۲۲', '۲۳'],
    today_data: [120000, 85000, 42000, 0, 0, 0, 35000, 210000, 450000, 620000, 780000, 910000, 1050000, 1250000, 1420000, 1680000, 1850000, 1920000, 1750000, 1480000, 1120000, 890000, 540000, 210000],
    yesterday_data: [98000, 67000, 31000, 0, 0, 0, 28000, 195000, 420000, 580000, 710000, 860000, 980000, 1150000, 1320000, 1550000, 1720000, 1810000, 1650000, 1380000, 1050000, 810000, 490000, 185000]
  },
  monthly: {
    labels: ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۱۰', '۱۱', '۱۲', '۱۳', '۱۴', '۱۵', '۱۶', '۱۷', '۱۸', '۱۹', '۲۰', '۲۱', '۲۲', '۲۳', '۲۴', '۲۵', '۲۶', '۲۷', '۲۸', '۲۹', '۳۰'],
    current_data: [1250000, 980000, 1420000, 2150000, 1880000, 2250000, 3120000, 2980000, 3450000, 3780000, 4020000, 3950000, 4210000, 4450000, 4680000, 4820000, 5050000, 5210000, 4980000, 4750000, 5120000, 5380000, 5550000, 5420000, 5680000, 5850000, 5920000, 5780000, 5610000, 5450000],
    previous_data: [1180000, 920000, 1350000, 1980000, 1720000, 2080000, 2850000, 2720000, 3180000, 3450000, 3680000, 3620000, 3850000, 4080000, 4250000, 4380000, 4620000, 4750000, 4520000, 4350000, 4680000, 4920000, 5080000, 4980000, 5150000, 5320000, 5380000, 5250000, 5100000, 4950000]
  },
  // برای بازه "هفته جاری"
  weekly: {
    labels: ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'],
    current_data: [3250000, 4120000, 5080000, 4750000, 6210000, 6980000, 7450000],
    previous_data: [2980000, 3850000, 4750000, 4420000, 5850000, 6550000, 7020000]
  }
}

export default {
  name: 'ComparisonChartSection',
  props: {
    title: {
      type: String,
      default: 'نمودار مقایسه‌ای تراکنش‌ها'
    },
    subtitle: {
      type: String,
      default: 'مقایسه فروش خالص؛ مرجوعی‌های ثبت‌شده از فروش کم می‌شوند'
    },
    // API endpoint (اختیاری، اگه داده نشد از دیتای پیش‌فرض استفاده می‌کنه)
    apiUrl: {
      type: String,
      default: ''
    },
    partnerId: {
      type: [String, Number],
      default: null
    },
    dataMapping: {
      type: Object,
      default: () => ({
        hourly: {
          labels: 'hourly_chart.labels',
          today: 'hourly_chart.data',
          yesterday: 'comparison.hourly_comparison.yesterday_data'
        },
        monthly: {
          labels: 'monthly_chart.labels',
          current: 'monthly_chart.data',
          previous: 'monthly_chart.previous_data'
        }
      })
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedPeriod: 'today', // 'today', 'week', 'month'
      selectedSource: 'both',  // اضافه شود: 'both', 'shop', 'pos'
      periodOptions: [
        { value: 'today', label: 'امروز' },
        { value: 'week', label: 'مقایسه هفتگی' },
        { value: 'month', label: 'مقایسه ماهانه' }
      ],
      sourceOptions: [  // اضافه شود
        { value: 'both', label: 'همه' },
        { value: 'shop', label: 'آنلاین' },
        { value: 'pos', label: 'حضوری' }
      ],
      chart: null,
      // داده‌های واقعی (از API یا پیش‌فرض)
      chartData: {
        today: { labels: [], dataset1: [], dataset2: [] },
        week: { labels: [], dataset1: [], dataset2: [] },
        month: { labels: [], dataset1: [], dataset2: [] }
      }
    }
  },
  watch: {
    partnerId() {
      this.fetchData()
    }
  },
  computed: {
    apiUrlWithSource() {
      let url = this.apiUrl || 'deliver/chart/chart-data'
      const sourceParam = this.selectedSource === 'both' ? '' : this.selectedSource
      if (sourceParam) {
        url += (url.includes('?') ? '&' : '?') + `source=${sourceParam}`
      }
      return url
    },
    dataset1Label() {
      if (this.selectedPeriod === 'today') return 'امروز'
      if (this.selectedPeriod === 'week') return 'هفته جاری'
      return 'ماه جاری'
    },
    dataset2Label() {
      if (this.selectedPeriod === 'today') return 'دیروز'
      if (this.selectedPeriod === 'week') return 'هفته قبل'
      return 'ماه قبل'
    },
    currentLabels() {
      return this.chartData[this.selectedPeriod]?.labels || []
    },
    currentDataset1() {
      return this.chartData[this.selectedPeriod]?.dataset1 || []
    },
    currentDataset2() {
      return this.chartData[this.selectedPeriod]?.dataset2 || []
    },
    currentDataset1Total() {
      return this.currentDataset1.reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    currentDataset2Total() {
      return this.currentDataset2.reduce((sum, v) => sum + (Number(v) || 0), 0)
    }
  },
  methods: {
    selectSource(source) {  // اضافه شود
      this.selectedSource = source
      this.fetchData()  // دوباره داده بگیر
    },
    formatNumber(value) {
      if (!value && value !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(value)
    },

    sanitizeArray(arr) {
      return (arr || []).map(v => {
        const n = Number(v)
        return Number.isFinite(n) ? n : 0
      })
    },

    async fetchData() {
      this.loading = true
      this.error = null

      if (typeof this.request === 'function') {
        let url = this.endpoint
            ? this.endpoint('deliver/chart/chart-data')
            : 'deliver/chart/chart-data'

        if (this.selectedSource !== 'both') {
          url += (url.includes('?') ? '&' : '?') + `source=${this.selectedSource}`
        }
        if (this.partnerId) {
          url += (url.includes('?') ? '&' : '?') + `partner_id=${encodeURIComponent(this.partnerId)}`
        }
        this.request({
          url: url,
          method: "get",
          success: data => {
            this.processApiData(data)
          },
          error: (err) => {
            console.error('API request failed:', err)
            this.loadDefaultData()
            this.loading = false
            this.$nextTick(() => this.initChart())
          }
        })
      } else {
        try {
          let url = this.apiUrl || '/api/deliver/chart/chart-data'
          if (this.selectedSource !== 'both') {
            url += (url.includes('?') ? '&' : '?') + `source=${this.selectedSource}`
          }
          if (this.partnerId) {
            url += (url.includes('?') ? '&' : '?') + `partner_id=${encodeURIComponent(this.partnerId)}`
          }
          const response = await fetch(url)
          if (!response.ok) throw new Error('Network response was not ok')
          const data = await response.json()
          this.processApiData(data)
        } catch (err) {
          console.error('Fetch failed:', err)
          this.loadDefaultData()
          this.loading = false
          this.$nextTick(() => this.initChart())
        }
      }
    },
    processApiData(data) {
      try {
        this.chartData = {
          today: {
            labels: data.hourly?.labels || DEFAULT_CHART_DATA.hourly.labels,
            dataset1: this.sanitizeArray(data.hourly?.today_data || DEFAULT_CHART_DATA.hourly.today_data),
            dataset2: this.sanitizeArray(data.hourly?.yesterday_data || DEFAULT_CHART_DATA.hourly.yesterday_data)
          },
          week: {
            labels: data.weekly?.labels || DEFAULT_CHART_DATA.weekly.labels,
            dataset1: this.sanitizeArray(data.weekly?.current_data || DEFAULT_CHART_DATA.weekly.current_data),
            dataset2: this.sanitizeArray(data.weekly?.previous_data || DEFAULT_CHART_DATA.weekly.previous_data)
          },
          month: {
            labels: data.monthly?.labels || DEFAULT_CHART_DATA.monthly.labels,
            dataset1: this.sanitizeArray(data.monthly?.current_data || DEFAULT_CHART_DATA.monthly.current_data),
            dataset2: this.sanitizeArray(data.monthly?.previous_data || DEFAULT_CHART_DATA.monthly.previous_data)
          }
        }
        this.loading = false
        this.$nextTick(() => this.initChart())
      } catch (err) {
        console.error('Error processing API data:', err)
        this.loadDefaultData()
        this.loading = false
        this.$nextTick(() => this.initChart())
      }
    },

    loadDefaultData() {
      this.chartData = {
        today: {
          labels: DEFAULT_CHART_DATA.hourly.labels,
          dataset1: this.sanitizeArray(DEFAULT_CHART_DATA.hourly.today_data),
          dataset2: this.sanitizeArray(DEFAULT_CHART_DATA.hourly.yesterday_data)
        },
        week: {
          labels: DEFAULT_CHART_DATA.weekly.labels,
          dataset1: this.sanitizeArray(DEFAULT_CHART_DATA.weekly.current_data),
          dataset2: this.sanitizeArray(DEFAULT_CHART_DATA.weekly.previous_data)
        },
        month: {
          labels: DEFAULT_CHART_DATA.monthly.labels,
          dataset1: this.sanitizeArray(DEFAULT_CHART_DATA.monthly.current_data),
          dataset2: this.sanitizeArray(DEFAULT_CHART_DATA.monthly.previous_data)
        }
      }
    },

    selectPeriod(period) {
      this.selectedPeriod = period
      this.updateChart()
    },

    getPeriodLabel() {
      const found = this.periodOptions.find(p => p.value === this.selectedPeriod)
      return found ? found.label : ''
    },

    updateChart() {
      if (!this.chart) return

      // بروزرسانی لیبل‌های محور X
      this.chart.data.labels = this.currentLabels
      // بروزرسانی داده‌های هر دو دیتاست
      this.chart.data.datasets[0].data = this.currentDataset1
      this.chart.data.datasets[1].data = this.currentDataset2
      // بروزرسانی لیبل‌های دیتاست‌ها (برای نمایش درست در tooltip)
      this.chart.data.datasets[0].label = this.dataset1Label
      this.chart.data.datasets[1].label = this.dataset2Label

      this.chart.update()
    },

    initChart() {
      if (typeof Chart === 'undefined') {
        console.error('Chart.js not loaded! Make sure to include Chart.js v2.9.4')
        return
      }

      const ctx = this.$refs.chartCanvas
      if (!ctx) return

      if (this.chart) this.chart.destroy()

      // ایجاد نمودار با استفاده از سینتکس Chart.js v2
      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.currentLabels,
          datasets: [
            {
              label: this.dataset1Label,
              data: this.currentDataset1,
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderWidth: 2.5,
              fill: true,
              tension: 0.3,
              pointBackgroundColor: '#10b981',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              pointRadius: 3.5,
              pointHoverRadius: 5
            },
            {
              label: this.dataset2Label,
              data: this.currentDataset2,
              borderColor: '#3D3D3D',
              backgroundColor: 'rgba(137,137,137, 0.05)',
              borderWidth: 2.5,
              fill: true,
              tension: 0.3,
              pointBackgroundColor: '#3D3D3D',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              pointRadius: 3.5,
              pointHoverRadius: 5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          tooltips: {
            mode: 'index',        // نمایش tooltip یکپارچه برای هر دو دیتاست
            intersect: false,     // با حرکت موس روی هر نقطه، tooltip نشان داده شود
            rtl: true,
            bodyFont: {
              family: 'Vazir, sans-serif',
              size: 11
            },
            titleFont: {
              family: 'Vazir, sans-serif',
              size: 12,
              weight: 'bold'
            },
            callbacks: {
              label: function(tooltipItem, data) {
                // دریافت لیبل دیتاست (امروز/دیروز یا هفته جاری/قبل یا ماه جاری/قبل)
                let label = data.datasets[tooltipItem.datasetIndex].label || ''
                let value = tooltipItem.yLabel
                let formatted = new Intl.NumberFormat('fa-IR').format(value)
                return `${label}: ${formatted} تومان`
              }
            }
          },
          legend: {
            display: false       // مخفی کردن legend پیش‌فرض چون خودمان بالای نمودار نشان می‌دهیم
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: false,
                color: 'rgba(75, 85, 99, 0.2)'
              },
              ticks: {
                color: '#9ca3af',
                font: {
                  family: 'Vazir, sans-serif',
                  size: 10
                }
              }
            }],
            yAxes: [{
              gridLines: {
                color: 'rgba(75, 85, 99, 0.2)',
                drawBorder: true
              },
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  // فرمت اعداد با کاما جداکننده هزارگان (بدون افزودن تومان در محور Y)
                  return this.formatNumber(value)
                },
                color: '#9ca3af',
                font: {
                  family: 'Vazir, sans-serif',
                  size: 10
                }
              }
            }]
          },
          title: {
            display: false
          }
        }
      })
    }
  },
  mounted() {
    this.fetchData()
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy()
  }
}
</script>